import { Link } from "@mui/material";

import { faqs, howItWorks, purchaseSeatLanguages } from "./helpers";
import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";

import Background from "public/images/brooklyncyclones/background.webp";
import StaticSeatMap from "public/images/brooklyncyclones/seatmap.webp";
import FindGamesBanner from "public/images/brooklyncyclones/PickaPlanLarge.jpg";

import type { TeamConfig } from "../types";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Brooklyn_Cyclones.png";

const config: TeamConfig = {
  teamSchema: "brooklyncyclones_flex",
  browserTitle: "Cyclones Amazin' Pick A Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Brooklyn Cyclones",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        discount: 15,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Congrats! You've unlocked 15% off your tickets! Add 1 more game for 20% off or 3+ games for 25% off plus a $20 Cyclones gift card.",
      },
      {
        id: 2,
        discount: 20,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        customProgressBarMessage:
          "Congrats! You've unlocked 20% off your tickets! Add 2 more games for 25% off plus a $20 Cyclones gift card or checkout now.",
      },
      {
        id: 3,
        discount: 20,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 25% off plus a $20 Cyclones gift card!",
      },
      {
        id: 4,
        discount: 25,
        minSelectedAmount: 5,
        updatedAccountGroup: 2,
        customAditionalMessage: " for 25% off plus a $20 Cyclones gift card?",
        customProgressBarMessage:
          "Congrats! You've unlocked 25% off your tickets plus a $20 Cyclones gift card! Add more games or checkout now.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "50%",
      marginX: "auto",
      display: "block",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isPurchaseCreditHistoryEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isStandAlonePurchaseCreditEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 9,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://mlb.tickets.com/?agency=BCYM_MYTIXX&orgid=58030",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    authBackgroundImage: Background.src,
    checkoutStatusPageImage: Background.src,
    findGamesBannerImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: false,
      showLogoWithTitle: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,

    progressBar: {
      staticProgressBarText: false,
    },

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          target="_blank"
          href="tel:7184498497"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (718) 372-5596
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#004a8d",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    redeemGiftCard: null,
    topNavMenuItems: null,
    purchaseGiftCard: null,
    purchaseSeats: purchaseSeatLanguages,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Brooklyn Cyclones tickets are confirmed! Your tickets will be available in your Cyclones My Tickets Account.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
